import { Box, Checkbox, Chip, useTheme } from "@mui/material";
import React, { useContext, useMemo, useState } from "react";

import SelectGroupsOfFeeds from "@/components/SelectGroupsOfFeeds.tsx";
import SelectNonGroups from "@/components/SelectNonGroups.tsx";
import { FullInput } from "@/components/Utils.tsx";
import locator from "@/locator.ts";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { feedsIHaveAccessTo, fetchWorkspaceCommandAliases, groupsQuery } from "@/models/repositories";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { lowerCaseToCapCase, matchesFilter } from "@/utils";

export default React.memo(function SelectRecipients({
  activeChannels,
  setActiveIds,
  toggleAllIds,
  allowNonAliasChannels,
  driverTableList,
}: {
  activeChannels: string[];
  setActiveIds: (value: string[]) => void;
  toggleAllIds: (value: string[]) => void;
  allowNonAliasChannels: boolean;
  driverTableList?: boolean;
}) {
  const theme = useTheme();
  const { currentWorkspaceId: workspaceId, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const myCurrentWorkspaceMembershipId = myCurrentWorkspaceMembership?.id;

  /**
   * STATE
   */
  const [selectedCommandAliases, setSelectedCommandAliases] = useState<string[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  /**
   * Live Queries
   */
  const groupList =
    useReactiveSelectQuery(
      groupsQuery,
      [{ workspaceId, myCurrentWorkspaceMembershipId }],
      "SelectRecipients:groupsQuery",
    ) ?? [];

  const commandAliasList =
    useReactiveSelectQuery(
      fetchWorkspaceCommandAliases,
      [{ workspaceId, workspaceMembershipId: myCurrentWorkspaceMembershipId }],
      "SelectRecipients:fetchWorkspaceCommandAliases",
    ) ?? [];

  const feedListWithGroups =
    useReactiveSelectQuery(
      feedsIHaveAccessTo,
      [
        {
          workspaceId,
          permissionName: "write",
          workspaceMembershipId: myCurrentWorkspaceMembershipId,
          allowNonAliasChannels,
          allowGroups: true,
        },
      ],
      "SelectRecipients:feedsWithGroupIds",
    ) ?? [];

  const feedListWithoutGroups =
    useReactiveSelectQuery(
      feedsIHaveAccessTo,
      [
        {
          workspaceId,
          permissionName: "write",
          workspaceMembershipId: myCurrentWorkspaceMembershipId,
          allowNonAliasChannels,
          allowGroups: false,
        },
      ],
      "SelectRecipients:feedListWithoutGroups",
    ) ?? [];

  /**
   * Transformers
   */
  const reducedAliases = useMemo(() => {
    return [...new Set(commandAliasList?.map((alias) => alias.alias))];
  }, [commandAliasList]);

  const feedGroupsFiltered = useMemo(() => {
    return groupList
      .map((group) => {
        const feeds = feedListWithGroups
          ?.filter((g) => g.groupId === group.id)
          ?.filter((feed) => matchesFilter(feed.title, filter))
          ?.map((feed) => ({
            ...feed,
            commandAlias: commandAliasList?.filter((alias) => alias.feedId === feed.id),
          }));
        return {
          ...group,
          feeds,
        };
      })
      .filter((group) => group?.feeds?.length > 0);
  }, [feedListWithGroups, groupList, filter, commandAliasList]);

  const filteredNonGroups = useMemo(() => {
    return feedListWithoutGroups
      ?.filter((feed) => matchesFilter(feed.title, filter))
      ?.map((feed) => ({
        ...feed,
        commandAlias: commandAliasList?.filter((alias) => alias.feedId === feed.id),
      }));
  }, [feedListWithoutGroups, filter, commandAliasList]);

  /**
   * State Methods
   */
  const toggleSingleFeed = (id: string) => {
    if (activeChannels?.includes(id)) {
      setActiveIds(activeChannels?.filter((feedId) => feedId !== id));
    } else {
      setActiveIds([...activeChannels, id]);
    }
  };

  const toggleSingleAlias = (alias: string) => {
    const flatMapGroups = feedGroupsFiltered?.map((group) => group.feeds.flat()).flat();
    const combinedList = [...flatMapGroups, ...filteredNonGroups];
    if (selectedCommandAliases?.includes(alias)) {
      setSelectedCommandAliases(selectedCommandAliases?.filter((a) => a !== alias));

      const removeActiveChannelsThatHaveActiveAlias = combinedList
        ?.filter((feed) => feed.commandAlias?.some((cmd) => cmd.alias === alias))
        ?.map((feed) => feed.id?.toString());

      const newActiveChannels = activeChannels?.filter((id) => !removeActiveChannelsThatHaveActiveAlias?.includes(id));
      setActiveIds(newActiveChannels);
    } else {
      const addedValues = combinedList
        ?.filter((feed) => feed.commandAlias?.some((cmd) => cmd.alias === alias))
        ?.map((feed) => feed.id?.toString())
        ?.concat(activeChannels);
      setSelectedCommandAliases([...selectedCommandAliases, alias]);
      const uniqueIds = [...new Set(addedValues)];
      setActiveIds(uniqueIds);
    }
  };

  const toggleOpenGroup = (groupId: string) => {
    setOpenGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
  };
  const toggleAllGroupFeeds = (ids: string[]) => toggleAllIds(ids);

  /**
   * Container Styles
   */
  const containerClass = {
    active: {
      background: theme.palette.secondary.main,
      borderRadius: "0.5rem",
      border: `solid 1px ${theme.palette.secondary.light}`,
    },
    inactive: {
      border: "solid 1px transparent",
    },
  };

  const activeChannelFeeds = useMemo(() => {
    if (!driverTableList) {
      return [];
    }
    const uniqueFeeds = [
      ...new Map([...feedListWithGroups, ...feedListWithoutGroups].map((feed) => [feed.id, feed])).values(),
    ];
    return uniqueFeeds.filter((feed) => activeChannels.includes(feed.id?.toString()));
  }, [feedListWithGroups, feedListWithoutGroups, activeChannels, driverTableList]);

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <FullInput
          aria-label={locator.feed.input.fleetMessage.modal.filterRecipients}
          placeholder={locator.feed.input.fleetMessage.modal.filterRecipients}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </Box>

      <Box
        sx={{
          maxHeight: "320px",
          width: "100%",
          overflowY: "auto",
        }}
      >
        {activeChannelFeeds?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ fontWeight: "bold", mb: 1 }}>Selected Drivers</Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              {activeChannelFeeds?.map((i) => (
                <Chip label={i.title} key={`selected-drivers-${i.id}`} size="small" />
              ))}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            gap: 2,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <SelectGroupsOfFeeds
              data={feedGroupsFiltered}
              index={0}
              openGroups={openGroups}
              activeChannels={activeChannels}
              toggleOpenGroup={toggleOpenGroup}
              toggleAllGroupFeeds={toggleAllGroupFeeds}
              toggleSingleFeed={toggleSingleFeed}
              containerClassList={containerClass}
            />

            <SelectNonGroups
              data={filteredNonGroups}
              index={1}
              activeChannels={activeChannels}
              toggleAllGroupFeeds={toggleAllGroupFeeds}
              toggleSingleFeed={toggleSingleFeed}
              openGroups={openGroups}
              toggleOpenGroup={toggleOpenGroup}
              containerClassList={containerClass}
            />
          </Box>
          <Box>
            <Box sx={{ minWidth: "160px" }}>
              {reducedAliases.map((commandAlias) => (
                <Box
                  key={`label-${commandAlias}`}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mb: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSingleAlias(commandAlias)}
                >
                  <Checkbox
                    sx={{ p: 0 }}
                    checked={selectedCommandAliases?.includes(commandAlias)}
                    aria-label="Select all"
                  />
                  <Box sx={{ grow: 1 }}>All {lowerCaseToCapCase(commandAlias)}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
